import { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Table, Badge, Spinner } from 'react-bootstrap';
import { useApi } from '../../../contexts/ApiProvider';

export default function HobbyBlueprint({ hobbyId }) {
    const api = useApi();
    const [fields, setFields] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [editId, setEditId] = useState(null);

    const defaultFieldState = {
        entity_type: 'Item',
        field_name: '',
        data_type: 'text',
        options: '',
        target_category: ''
    };

    const [newField, setNewField] = useState(defaultFieldState);

    useEffect(() => {
        fetchFields();
        // eslint-disable-next-line
    }, [hobbyId]);

    const fetchFields = async () => {
        setIsLoading(true);
        const res = await api.get(`/hobbies/${hobbyId}/fields`);
        if (res.ok) setFields(res.body.items || []);
        setIsLoading(false);
    };

    const openEditForm = (field) => {
        setEditId(field.id);
        setNewField({
            entity_type: field.entity_type,
            field_name: field.field_name,
            data_type: field.data_type,
            // Convert the options array back into a comma-separated string for the input box
            options: field.options ? field.options.join(', ') : '',
            target_category: field.target_category || ''
        });

        // Scroll to the top right so the user sees the form populated
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const cancelEdit = () => {
        setEditId(null);
        setNewField(defaultFieldState);
    };

    const handleAddField = async (e) => {
        e.preventDefault();

        // Parse options for both dropdowns and item references (category filters), and units for numbers
        let parsedOptions = [];
        if (['dropdown', 'item_reference', 'multi_item_reference'].includes(newField.data_type) && newField.options) {
            parsedOptions = newField.options.split(',').map(opt => opt.trim()).filter(opt => opt !== '');
        } else if (newField.data_type === 'number' && newField.options) {
            parsedOptions = [newField.options.trim()];
        }

        const payload = {
            entity_type: newField.entity_type,
            field_name: newField.field_name,
            data_type: newField.data_type,
            options: parsedOptions.length > 0 ? parsedOptions : null,
            target_category: newField.target_category.trim() !== '' ? newField.target_category.trim() : null
        };

        const res = editId
            ? await api.put(`/hobbies/${hobbyId}/fields/${editId}`, payload)
            : await api.post(`/hobbies/${hobbyId}/fields`, payload);

        if (res.ok) {
            cancelEdit();
            fetchFields();
        } else {
            alert("Failed to save custom field.");
        }
    };

    const handleDeleteField = async (fieldId) => {
        if (!window.confirm("Remove this field from future forms? Historical data will be preserved.")) return;
        const res = await api.delete(`/hobbies/${hobbyId}/fields/${fieldId}`);
        if (res.ok) fetchFields();
    };

    const itemFields = fields.filter(f => f.entity_type === 'Item');
    const projectFields = fields.filter(f => f.entity_type === 'Project');
    const logFields = fields.filter(f => f.entity_type === 'Log');

    const renderTable = (entityFields, title, icon, emptyText) => (
        <Card className="border-0 shadow-sm rounded-4 mb-4">
            <Card.Body className="p-4">
                <h6 className="fw-bold mb-3 border-bottom pb-2">
                    <i className={`bi ${icon} text-primary me-2`}></i>{title} Fields
                </h6>
                {entityFields.length === 0 ? (
                    <div className="text-muted small py-2">{emptyText}</div>
                ) : (
                    <Table hover responsive borderless className="align-middle mb-0">
                        <thead>
                            <tr className="text-muted small border-bottom">
                                <th>Field Name</th>
                                <th>Data Type</th>
                                <th>Settings</th>
                                <th>Category</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {entityFields.map(f => (
                                <tr key={f.id} className="border-bottom">
                                    <td className="fw-bold">{f.field_name}</td>
                                    <td>
                                        <Badge bg="light" text="dark" className="border text-uppercase">{f.data_type}</Badge>
                                    </td>
                                    <td className="small text-muted">
                                        {f.data_type === 'dropdown' && f.options ? (
                                            <>Options: {f.options.join(', ')}</>
                                        ) : ['item_reference', 'multi_item_reference'].includes(f.data_type) && f.options ? (
                                            <>Filter: {f.options.join(', ')}</>
                                        ) : f.data_type === 'number' && f.options && f.options.length > 0 ? (
                                            <><i className="bi bi-ruler me-1"></i>Unit: {f.options[0]}</>
                                        ) : '--'}
                                    </td>
                                    <td>
                                        {f.target_category ? (
                                            <Badge bg="info" className="small fw-normal">Only: {f.target_category}</Badge>
                                        ) : (
                                            <span className="text-muted small">All Categories</span>
                                        )}
                                    </td>
                                    <td className="text-end text-nowrap pe-3">
                                        <Button variant="link" className="text-primary p-0 me-3" onClick={() => openEditForm(f)}>
                                            <i className="bi bi-pencil-square hover-scale"></i>
                                        </Button>
                                        <Button variant="link" className="text-danger p-0" onClick={() => handleDeleteField(f.id)}>
                                            <i className="bi bi-trash3 hover-scale"></i>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );

    if (isLoading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;

    return (
        <div className="fade-in">
            <Row className="g-4">
                <Col md={8}>
                    {renderTable(projectFields, 'Project', 'bi-kanban', 'No specific tracking metrics defined for active projects.')}
                    {renderTable(logFields, 'Activity Log', 'bi-list-check', 'No custom metrics defined for timeline logs.')}
                    {renderTable(itemFields, 'Inventory & Tools', 'bi-box-seam', 'No custom attributes defined for items.')}
                </Col>

                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 sticky-top" style={{ top: '80px', zIndex: 1 }}>
                        <Card.Body className="p-4 bg-light rounded-4">
                            <h6 className="fw-bold mb-1"><i className="bi bi-stars text-warning me-2"></i>Add Custom Field</h6>
                            <p className="small text-muted mb-4 border-bottom pb-3">Expand your blueprint to track new data points.</p>

                            <Form onSubmit={handleAddField}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-muted mb-1">Attach to...</Form.Label>
                                    <Form.Select value={newField.entity_type} onChange={e => setNewField({...newField, entity_type: e.target.value})}>
                                        <option value="Project">A Project / Batch</option>
                                        <option value="Log">An Activity Log Update</option>
                                        <option value="Item">An Inventory Item / Tool</option>
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-muted mb-1">
                                        {newField.entity_type === 'Log' ? 'Apply only to Log Type? (Optional)' : 'Apply only to Category? (Optional)'}
                                    </Form.Label>
                                    <Form.Control type="text" placeholder="e.g., SCOBY, Seed, Tool" value={newField.target_category} onChange={e => setNewField({...newField, target_category: e.target.value})} />
                                    <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>Leave blank to apply to ALL {newField.entity_type}s.</Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-muted mb-1">Field Name</Form.Label>
                                    <Form.Control type="text" placeholder="e.g., Humidity, Acidity..." value={newField.field_name} onChange={e => setNewField({...newField, field_name: e.target.value})} required />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-muted mb-1">Data Type</Form.Label>
                                    <Form.Select value={newField.data_type} onChange={e => setNewField({...newField, data_type: e.target.value})}>
                                        <option value="text">Text (Short or Long)</option>
                                        <option value="number">Number / Decimal</option>
                                        <option value="boolean">Toggle (Yes/No)</option>
                                        <option value="dropdown">Dropdown List</option>
                                        <option value="item_reference">Link to an Inventory Item</option>
                                        <option value="multi_item_reference">Link to Multiple Inventory Items</option>
                                        {/* --- NEW OPTION --- */}
                                        <option value="datetime">Date & Time</option>
                                    </Form.Select>
                                </Form.Group>

                                {/* This is where the user types "SCOBY" to set the filter rule */}
                                {['dropdown', 'item_reference', 'multi_item_reference'].includes(newField.data_type) && (
                                    <Form.Group className="mb-4 fade-in">
                                        <Form.Label className="small fw-bold text-muted mb-1">
                                            {newField.data_type === 'dropdown' ? 'Dropdown Options' : 'Category Filter (Optional)'}
                                        </Form.Label>
                                        <Form.Control type="text" placeholder={newField.data_type === 'dropdown' ? "e.g., F1, F2, F3" : "e.g., SCOBY, Tea"} value={newField.options} onChange={e => setNewField({...newField, options: e.target.value})} required={newField.data_type === 'dropdown'} />
                                        <Form.Text className="text-muted">
                                            {newField.data_type === 'dropdown'
                                                ? "Separate options with a comma."
                                                : "Only show items from this category. Separate multiple with a comma."}
                                        </Form.Text>
                                    </Form.Group>
                                )}

                                {/* --- NEW: UNIT INPUT FOR NUMBERS --- */}
                                {newField.data_type === 'number' && (
                                    <Form.Group className="mb-4 fade-in">
                                        <Form.Label className="small fw-bold text-muted mb-1">Unit of Measurement (Optional)</Form.Label>
                                        <Form.Control type="text" placeholder="e.g., cm, oz, grams, days" value={newField.options} onChange={e => setNewField({...newField, options: e.target.value})} />
                                    </Form.Group>
                                )}

                                <div className="d-flex gap-2 mt-3">
                                    <Button variant="primary" type="submit" className="w-100 fw-bold rounded-pill">
                                        {editId ? 'Save Changes' : 'Add to Blueprint'}
                                    </Button>
                                    {editId && (
                                        <Button variant="light" type="button" className="w-100 fw-bold rounded-pill border" onClick={cancelEdit}>
                                            Cancel
                                        </Button>
                                    )}
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
